import { FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Container,
    LoadingOverlay,
    Paper,
    PasswordInput,
    Text,
    Title,
} from "@mantine/core";
import { Auth } from "aws-amplify";
import type { TFunction } from "i18next";
import { TranslationKeys } from "src/types/i18next";
import { handleError } from "src/utils/error";

import { FormItem } from "../components/FormItem";

const translateAuthErrorMessage = (t: TFunction, message: string): TranslationKeys | undefined => {
    if (message.indexOf("empty") >= 0) {
        return "password_error_empty";
    }

    if (message.indexOf("long enough") >= 0) {
        return "password_error_length";
    }

    if (message.indexOf("uppercase") >= 0) {
        return "password_error_uppercase";
    }

    if (message.indexOf("lowercase") >= 0) {
        return "password_error_lowercase";
    }

    if (message.indexOf("numeric") >= 0) {
        return "password_error_number";
    }

    if (message.indexOf("symbol") >= 0) {
        return "password_error_symbol";
    }

    return undefined;
};

export interface PasswordResetPageProps { }
export const PasswordResetPage = ({ }: PasswordResetPageProps) => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");

    const [isReseting, setIsReseting] = useState(false);
    const [isDone, setIsDone] = useState(false);
    // This confirm page is only usable when opened from the emailed reset link,
    // which carries the ?token= (base64 {email, code}). Without a valid token we
    // must not render the form — otherwise submit calls forgotPasswordSubmit with
    // an empty username and Amplify reports a misleading "password cannot be
    // empty" (TTV2-1504).
    const [isTokenValid, setIsTokenValid] = useState(() => Boolean(token));

    useEffect(() => {
        try {
            const { email, code } = JSON.parse(atob(token));

            if (!email || !code) {
                throw new Error("Reset token is missing email or code");
            }

            setEmail(email);
            setCode(code);
            setIsTokenValid(true);
        } catch (err) {
            setIsTokenValid(false);
            handleError(err, { context: { token }, silent: true });
        }
    }, [token]);

    const handleSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();

        if (!email || !code) {
            setIsTokenValid(false);
            return;
        }

        setIsReseting(true);

        try {
            await Auth.forgotPasswordSubmit(email, code, newPassword);
            setIsDone(true);
        } catch (err) {
            // Cognito service errors (InvalidPasswordException from cognito-idp) carry
            // the reason in err.message; client-side AuthErrors carry it in err.log.
            // JSON.stringify on an Error yields "{}" (message/name are non-enumerable),
            // which is why the specific reason was previously lost and only the generic
            // title showed.
            const rawMessage = String(err?.message ?? err?.log ?? "");
            const message: TranslationKeys | undefined =
                err?.name === "ExpiredCodeException"
                    ? "resetpassword_error_expired"
                    : translateAuthErrorMessage(t, rawMessage);

            handleError(err, {
                context: { token },
                title: { key: "resetpassword_failed", namespace: "common" },
                // Show the specific localized reason when recognised; otherwise fall back
                // to the raw Cognito message so the user always sees why it failed.
                ...(message
                    ? { message: { key: message, namespace: "common" } }
                    : { additionalMessage: rawMessage || undefined }),
            });
        } finally {
            setIsReseting(false);
        }
    }, [email, code, newPassword, token, t]);

    return (
        <Container size={420} my={40}>
            <Title
                align="center"
                sx={(theme) => ({
                    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
                    fontWeight: 900,
                })}
            >
                {t("signin_title")}
            </Title>
            <Text
                color="dimmed"
                size="sm"
                align="center"
                mt={5}
            >
                {t("signin_subtitle")}
            </Text>
            <Paper
                mt={30}
                p={30}
                pos="relative"
                shadow="md"
                radius="md"
                component="form"
                onSubmit={handleSubmit}
                withBorder
            >
                <LoadingOverlay
                    overlayBlur={2}
                    radius="md"
                    visible={isReseting}
                />
                <Title mb="sm" order={3} align="center">
                    {t("resetpassword")}
                </Title>
                {!isTokenValid ? (
                    <>
                        <Text mb="md" ta="center">
                            {t("resetpassword_error_expired")}
                        </Text>
                        <Button
                            onClick={() =>
                                navigate("/")}
                            fullWidth
                        >
                            {t("signin")}
                        </Button>
                    </>
                ) : isDone ? (
                    <>
                        <Text mb="md" ta="center">
                            {t("resetpassword_done")}
                        </Text>
                        <Button
                            onClick={() =>
                                navigate("/")}
                            fullWidth
                        >
                            {t("signin")}
                        </Button>
                    </>
                ) : (
                    <>
                        <FormItem mb="xs" label={t("email")}>
                            {email}
                        </FormItem>
                        <PasswordInput
                            label={t("password_new")}
                            placeholder={t("password_new")}
                            value={newPassword}
                            onChange={event =>
                                setNewPassword(event?.target?.value)}
                        />
                        <Button
                            mt="md"
                            type="submit"
                            fullWidth
                        >
                            {t("resetpassword_setnew")}
                        </Button>
                    </>
                )}
            </Paper>
        </Container>
    );
};
